import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MediaInput, MediaInputValue } from '../../../common/media-input';
import { uploadMediaInputFiles } from '../../../../services/file-service';
import { AlertService } from '../../../../services/alert.service';
import { InputWrapper } from '../../../common/input-wrapper';
import { decimalValidation, required } from '../../../../utils/validations';
import { trpcSwr } from '../../../../api/trpc';
import classNames from 'classnames';
import { useIntl } from 'react-intl';

type RequestEditFormProps = {
  id: string;
  defaultValues: {
    name: string;
    files?: MediaInputValue[];
    weight: number | null;
    volume: number | null;
    city: string | null;
    price: number | null;
    amount: number | null;
  };
  onSave: () => void;
  onCancel: () => void;
};

export const CustomDeliveryRequestEditForm: React.FC<RequestEditFormProps> = (
  props,
) => {
  const intl = useIntl();

  const { trigger } = trpcSwr.customsDelivery.updateOne.useSWRMutation();
  const { control, register, handleSubmit, formState } = useForm<{
    name: string;
    weight: number | null;
    volume: number | null;
    city: string | null;
    price: number | null;
    amount: number | null;
    files: MediaInputValue[];
    link: string | null;
  }>({
    defaultValues: props.defaultValues,
  });

  const onSubmit = handleSubmit(async (form) => {
    const files = await uploadMediaInputFiles(form.files || []);
    await trigger({
      id: props.id,
      name: form.name.trim(),
      price: form.price ? +form.price : undefined,
      amount: form.amount ? +form.amount : undefined,
      weight: form.weight ? +form.weight : undefined,
      volume: form.volume ? +form.volume : undefined,
      city: form.city ?? undefined,
      files: files.map((file) => ({ id: file.file.id })),
    });
    AlertService.success();
    props.onSave();
  });

  function handleCancel() {
    props.onCancel();
  }

  return (
    <form onSubmit={onSubmit}>
      <div className="d-flex flex-row justify-content-between">
        <div className="fs-18 fw-600 color-gray-450">О заказе</div>
        <div>
          <button
            className="btn btn-link text-decoration-none"
            disabled={formState.isSubmitting}
          >
            {formState.isSubmitting ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              'Сохранить'
            )}
          </button>
          <button
            className="btn btn-link text-decoration-none"
            type="button"
            disabled={formState.isSubmitting}
            onClick={handleCancel}
          >
            Отменить
          </button>
        </div>
      </div>

      <InputWrapper
        theme="light"
        className="mt-3"
        title="Название"
        error={formState.errors.name?.message}
      >
        <input
          type="text"
          placeholder="Название"
          className="form-control"
          {...register('name', { ...required })}
        />
      </InputWrapper>

      <Controller
        control={control}
        name="files"
        render={({ field, fieldState }) => (
          <InputWrapper
            theme="light"
            className="mt-3"
            title="Файлы"
            error={fieldState.error?.message}
          >
            <MediaInput
              {...field}
              className="mt-2"
              multiple
              fileTypes={[
                'application/msword',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'image/png',
                'image/jpeg',
                'video/*',
                'application/pdf',
              ]}
            />
          </InputWrapper>
        )}
      />

      <Controller
        control={control}
        name={`amount`}
        rules={{
          required: intl.formatMessage({
            defaultMessage: 'Укажите количество штук',
            id: 'CustomsDeliveryItemForm.input-label.specifyAmount',
            description: 'Пояснение к полю ввода',
          }),
        }}
        render={({ field, fieldState }) => (
          <InputWrapper
            required
            title={intl.formatMessage({
              defaultMessage: 'Количество штук',
              id: 'CustomsDeliveryItemForm.input-label.amount',
              description: 'Заголовок поля ввода',
            })}
            className="mb-4 text-base-lh-100"
          >
            <div className="relative mb-2 rounded d-flex gap-2">
              <input
                defaultValue={field.value || undefined}
                onChange={field.onChange}
                type="number"
                min={1}
                step={1}
                autoComplete="off"
                className={classNames(
                  fieldState.error ? 'border-red-500' : 'border-gray-300',
                  'block w-full rounded border  text-left text-base-lh-125 text-gray-800 form-control',
                )}
              />
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center py-1 pr-3">
                <span className="text-base-lh-125 text-gray-400">шт</span>
              </div>
            </div>
          </InputWrapper>
        )}
      />

      <Controller
        control={control}
        rules={{
          required: intl.formatMessage({
            defaultMessage: 'Укажите стоимость товаров',
            id: 'CustomsDeliveryItemForm.input-label.specifyTheCostOfGoods',
            description: 'Пояснение к полю ввода',
          }),
        }}
        name={`price`}
        render={({ field, fieldState }) => (
          <InputWrapper
            required
            error={fieldState.error?.message}
            title={intl.formatMessage({
              defaultMessage: 'Общая стоимость товаров',
              id: 'CustomsDeliveryItemForm.input-label.totalCostOfGoods',
              description: 'Заголовок поля ввода',
            })}
            className="mb-4 text-base-lh-100"
          >
            <div className="relative  rounded d-flex gap-2 h-auto">
              <input
                defaultValue={field.value || undefined}
                onChange={field.onChange}
                className={classNames(
                  fieldState.error ? 'border-red-500' : 'border-gray-300',
                  'block w-full rounded border text-left text-base-lh-125 text-gray-800 form-control ',
                )}
                aria-describedby="price-currency"
                inputMode="decimal"
                autoComplete="off"
                placeholder="0"
                type="text"
                data-testid="input-totalCost"
              />
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center py-1 pr-3">
                <span className="text-base-lh-125 text-gray-400">¥</span>
              </div>
            </div>
          </InputWrapper>
        )}
      />

      <InputWrapper
        theme="light"
        className="mt-3"
        title="Вес"
        error={formState.errors.volume?.message}
      >
        <div className="input-group">
          <input
            className="form-control"
            type="number"
            min={0}
            step={0.01}
            {...register('weight', { ...required, ...decimalValidation })}
          />
          <span className="input-group-text">кг</span>
        </div>
      </InputWrapper>
      <InputWrapper
        theme="light"
        className="mt-3"
        title="Объём"
        error={formState.errors.volume?.message}
      >
        <div className="input-group">
          <input
            className="form-control"
            type="number"
            min={0}
            step={0.01}
            {...register('volume', { ...required, ...decimalValidation })}
          />
          <span className="input-group-text">м³</span>
        </div>
      </InputWrapper>
    </form>
  );
};
